import React, { useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import PartLabelTemplate from "../stock/PartLabelTemplate";
import PartLabelCoreTemplate from "../stock/PartLabelCoreTemplate";
import useCustomerDetails from "../../data/useCustomerDetails";
import { PartLabelDisplay } from "../../models/LabelDisplay";
import { samplePartLabelProps } from "../../utils/partTagFields";

// A stand-in QR so the preview never makes a network request.
const PLACEHOLDER_QR_SRC =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" shape-rendering="crispEdges">' +
      '<rect width="21" height="21" fill="#fff"/>' +
      '<path fill="#000" d="M0 0h7v7H0zM1 1v5h5V1zM2 2h3v3H2zM14 0h7v7h-7zM15 1v5h5V1zM16 2h3v3h-3zM0 14h7v7H0zM1 15v5h5v-5zM2 16h3v3H2z' +
      'M9 0h1v1H9zM11 0h1v2h-1zM9 2h2v1H9zM8 4h1v2H8zM10 4h2v1h-2zM9 6h1v1H9zM11 6h1v1h-1z' +
      'M0 9h2v1H0zM3 8h1v2H3zM5 9h2v1H5zM8 8h2v1H8zM11 8h1v2h-1zM13 9h1v1h-1zM15 8h2v1h-2zM18 9h1v1h-1zM20 8h1v2h-1z' +
      'M0 11h1v1H0zM2 11h2v1H2zM5 11h1v1H5zM8 10h1v2H8zM10 11h2v1h-2zM13 11h1v1h-1zM16 10h1v2h-1zM18 11h2v1h-2z' +
      'M9 13h1v1H9zM11 12h1v2h-1zM8 15h2v1H8zM11 15h1v1h-1zM13 13h2v1h-2zM16 13h1v2h-1zM18 13h3v1h-3zM20 15h1v1h-1z' +
      'M9 17h1v1H9zM11 17h2v1h-2zM8 19h1v2H8zM10 19h1v1h-1zM12 19h1v2h-1zM14 17h1v2h-1zM16 17h2v1h-2zM19 17h1v1h-1zM15 19h2v1h-2zM18 19h3v1h-3zM14 20h1v1h-1z"/>' +
      "</svg>"
  );

const RS_CORE_LOGO_SRC =
  "https://sawilmasharedstoragepuks.blob.core.windows.net/public/images/logos/rs/rs_core_logo.svg";

interface PartTagPreviewProps {
  display: PartLabelDisplay;
  isCore: boolean;
}

export default function PartTagPreview({ display, isCore }: PartTagPreviewProps) {
  const { brand } = useCustomerDetails();
  const logoSrc = brand?.data?.data.logo ?? "";

  const html = useMemo(
    () =>
      "<!doctype html>" +
      renderToStaticMarkup(
        React.createElement(isCore ? PartLabelCoreTemplate : PartLabelTemplate, {
          display,
          logoSrc,
          secondLogoSrc: isCore ? RS_CORE_LOGO_SRC : undefined,
          qrSrc: PLACEHOLDER_QR_SRC,
          ...samplePartLabelProps,
        })
      ),
    [display, isCore, logoSrc]
  );

  const widthCm = display.widthCm ?? 10;
  const heightCm = display.heightCm ?? 5;

  // Printing writes the label into its own iframe document, so the preview does the same.
  // Injecting it into the page instead would pick up the dashboard's base styles (block images,
  // max-width, line-height) and draw a label that never comes off the printer.
  return (
    <div className="overflow-x-auto">
      <iframe
        title="Part tag preview"
        srcDoc={html}
        sandbox=""
        scrolling="no"
        className="block border border-dashed border-gray-300 bg-white"
        // A pixel of slack each way: cm sizes round to fractional pixels and a fraction over
        // the frame is enough for a scrollbar.
        style={{ width: `calc(${widthCm}cm + 2px)`, height: `calc(${heightCm}cm + 2px)` }}
      />
    </div>
  );
}
